import { isExpiredEndOfDay } from './dateTime.js'

/**
 * Determines the actual status of an insurance policy by checking both
 * the status field and the end_date. If the end_date has passed, the
 * insurance is considered expired regardless of the status field value.
 *
 * The end date is inclusive: a policy ending on 13.07 stays active for the
 * whole of 13.07 and only becomes expired from 14.07 onwards.
 *
 * @param {Object} insuranceItem - The insurance line item
 * @returns {string} The actual insurance status ('active', 'expired', 'pending', 'cancelled', etc.)
 */
export function getInsuranceStatus(insuranceItem) {
  const insurance = insuranceItem?.metadata?.insurance

  if (insurance?.status === 'active' && isExpiredEndOfDay(insurance.end_date)) {
    return 'expired'
  }

  return insurance?.status
}

/**
 * VIU+ deductible schedule (Chubb contract): 30% of the insured product price
 * for theft/loss claims, 20% for all other claim reasons.
 *
 * @param {string} reason - The claim reason ('theft', 'loss', 'damage', ...)
 * @returns {number} The deductible rate (0.3 or 0.2)
 */
export function getDeductibleRate(reason) {
  return ['theft', 'loss'].includes(reason) ? 0.3 : 0.2
}

/**
 * The deductible percentage persisted into the claim metadata. Kept as literal
 * integers — deriving it as rate * 100 would leak float artifacts
 * (0.3 * 100 === 30.000000000000004) into the Chubb record.
 *
 * @param {string} reason - The claim reason
 * @returns {number} 30 for theft/loss, 20 otherwise
 */
export function getDeductiblePercentage(reason) {
  return ['theft', 'loss'].includes(reason) ? 30 : 20
}

/**
 * Calculates the deductible (Selbstbehalt) for a claim. Unrounded on purpose —
 * downstream consumers round to cents where needed.
 *
 * @param {number} baseAmount - The insured product price the contract is based on
 * @param {string} reason - The claim reason
 * @returns {number} The deductible amount
 */
export function calculateDeductible(baseAmount, reason) {
  return baseAmount * getDeductibleRate(reason)
}

/**
 * Returns the contractual deductible base amount if it was resolved for the
 * given policy, otherwise null (callers fall back to current-order prices).
 *
 * @param {Object|null} contractBase - afterSalesStore.insuranceContractBase
 * @param {string|undefined} policyId - The policy of the item being claimed
 * @returns {number|null} The contractual base amount or null
 */
export function getContractBaseAmount(contractBase, policyId) {
  if (!contractBase || !policyId || contractBase.policyId !== policyId) {
    return null
  }
  return contractBase.amount
}

/**
 * Whether an insurance line item is a policy currently in force.
 *
 * @param {Object} insuranceItem - The insurance line item
 * @returns {boolean} true when the policy is active and not past its end date
 */
export function insuranceIsActive(insuranceItem) {
  return getInsuranceStatus(insuranceItem) === 'active'
}
